<script setup lang="ts">
    import type { DialogContentEmits, DialogContentProps } from 'reka-ui';
    import type { HTMLAttributes } from 'vue';
    import { reactiveOmit } from '@vueuse/core';
    import { XMarkIcon } from '@heroicons/vue/20/solid';
    import { DialogClose, DialogContent, DialogPortal, useForwardPropsEmits } from 'reka-ui';
    import DialogOverlay from './DialogOverlay.vue';
    import { cn } from '../../utils';
    import Button from '../../Button/Button.vue';

    const props = withDefaults(defineProps<DialogContentProps & { class?: HTMLAttributes['class']; showCloseButton?: boolean }>(), {
        showCloseButton: true,
    });
    const emits = defineEmits<DialogContentEmits>();

    defineOptions({
        inheritAttrs: false,
    });

    const delegatedProps = reactiveOmit(props, 'class');

    const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <DialogPortal>
        <DialogOverlay />
        <DialogContent
            data-slot="dialog-content"
            v-bind="{ ...$attrs, ...forwarded }"
            :class="
                cn(
                    // Without the scroll, `max-h-screen` simply clips: a dialog taller than the
                    // viewport loses its footer with no way to reach it.
                    'max-h-screen overflow-y-auto',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    'bg-background-primary',
                    'fixed top-[50%] left-[50%] z-50 w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border shadow-lg duration-200 sm:max-w-lg',
                    'p-[24px] has-data-[slot=dialog-header]:pt-0 has-data-[slot=dialog-footer]:pb-0',
                    props.class,
                )
            "
        >
            <slot />

            <DialogClose
                v-if="showCloseButton"
                data-slot="dialog-close"
                class="absolute top-4 right-4"
                as-child
            >
                <Button
                    variant="secondary"
                    icon
                    size="sm"
                >
                    <XMarkIcon />
                    <span class="sr-only">Close</span>
                </Button>
            </DialogClose>
        </DialogContent>
    </DialogPortal>
</template>
